import { Component, HostListener, Inject, OnInit, Optional, ViewChild, ElementRef, WritableSignal, signal, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { RequestsService } from '../shared/services/requests.service';
import { SnackbarService } from '../shared/services/snackbar.service';
import { JournalResponseDto } from '../shared/models/journalResponseDto';
import { AttachmentData, RequestDocAttachmentResponse } from '../shared/models/requestDocAttachment';
import { RequestWorkflowDto } from '../shared/models/request-stage';
import { ReturnReasonDialogComponent } from '../return-reason-dialog/return-reason-dialog.component';
import { ApprovalDetails } from '../shared/models/ApprovalDetails';
import { RequestType, RequestTypeOrder } from '../enums/request-type.enum';
import { AuditService } from '../shared/services/audit.service';
import { StagesLabel } from '../enums/stages.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { ManageUserRoleService } from '../shared/services/manage-user-role.service';
import { UserRole } from '../shared/models/user-role';
import { BankAccountService } from '../shared/services/bank-account.service';
import { BankAccount } from '../shared/models/bankAccount';
import { validateJournalTransfer } from '../shared/utils/journal-validation';
import { RpaStatus } from '../enums/rpa-status.enum';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const STAGE_PARTNER = 3;
const STAGE_ACCOUNTS = 4;
const STAGE_COMPLETED = 5;

@Component({
    selector: 'xp-view-journal-request',
    templateUrl: './view-journal-request.component.html',
    styleUrl: './view-journal-request.component.scss'
})
export class ViewJournalRequestComponent implements OnInit, OnDestroy {
    @ViewChild('dialogContent', { read: ElementRef }) dialogContent!: ElementRef;
    currentDate: Date = new Date(Date.now());
    RequestType = RequestType;
    journalRequestForm!: FormGroup;
    journalData!: JournalResponseDto;
    displayFile: string[] = [];
    fileNames: AttachmentData[] = [];
    disableButtons: Boolean = false;
    approveBtn: string = "Accept";
    previousStages: RequestWorkflowDto[] = [];
    fileToBeDownloaded!: RequestDocAttachmentResponse;
    disableAtStage4 = false;
    showReturnReason: boolean = false;
    isAmountReleased: boolean = false;
    isAmountReleasedDisabled: boolean = false;
    showFundsCheckedByPartner: boolean = false;
    FundsCheckedPartnerText: boolean = false;
    enableAtPartnerStage = false;
    enableAtAccountsStage = false;
    isLastAuditRpaSuccessful: boolean = false;
    StagesLabel = StagesLabel;
    isDialogOpen: WritableSignal<boolean> = signal(false);
    isJournalRequestLoading: WritableSignal<boolean> = signal(true);
    matterFromNo!: number;
    matterToNo!: number;
    actionstepBaseUrl: string = '';
    showLinkToOpenMatter1: WritableSignal<boolean> = signal(false);
    showLinkToOpenMatter2: WritableSignal<boolean> = signal(false);
    filteredBankAccounts: BankAccount[] = [];
    actionstepMatterOverviewUrl1: string = '';
    actionstepMatterOverviewUrl2: string = '';
    actionstepOverviewBaseUrl: string = '';
    actionstepMatterUrl1: string = '';
    actionstepMatterUrl2: string = '';
    bankAccounts: BankAccount[] = [];
    isStagePartner2Return = false;
    isRejectionRequested: boolean = false;

    private readonly destroy$ = new Subject<void>();
    private readonly boundStorageHandler = this.onStorageEvent.bind(this);

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public data: JournalResponseDto,
        private formBuilder: FormBuilder,
        private dialog: MatDialog,
        private requestService: RequestsService,
        private snackbarService: SnackbarService,
        private auditService: AuditService,
        @Optional() public dialogRef: MatDialogRef<ViewJournalRequestComponent>,
        private router: Router,
        private route: ActivatedRoute,
        private manageUserRoleService: ManageUserRoleService,
        private bankAccountService: BankAccountService
    ) {
        this.isDialogOpen.set(!!this.data);
        this.journalRequestForm = this.formBuilder.group({
            returnReason: [''],
            matterFromNumber: '',
            matterToNumber: '',
            matterFrom: '',
            matterTo: '',
            matterFromName: '',
            matterToName: '',
            reason: '',
            amount: '',
            dateTransaction: [new Date(), Validators.required],
            dateAuthorized: '',
            dateRequested: '',
            requestedTime: [{ value: '', disabled: true }],
            transactionTime: [{ value: '' }], paymentMethod: [''],
            isCheckedLedger: false,
            uploadedFiles: [[]],
            comments: '',
            clientBankAccount1: ['', Validators.required],
            clientBankAccount2: ['', Validators.required],
            partnerAuthorising: '',
            fundsCheckedByPartner: [false, Validators.requiredTrue],
        });

        if (dialogRef) {
            dialogRef.disableClose = true;
        }
    }

    async ngOnInit() {
        if (this.data) {
            this.journalData = this.data;
            await this.resolveCreatorPartnerApprove();
        } else {
            const id = Number(this.route.snapshot.paramMap.get('id'));

            if (!id) {
                console.error('No ID in route');
                this.snackbarService.errorSnackBar('No Id');
                return;
            }

            await this.requestService.GetJournalRequestBasedOnId(id).then((res: JournalResponseDto) => {
                this.journalData = res;
                this.isJournalRequestLoading.set(false);
            });

            await this.resolveCreatorPartnerApprove();
        }

        this.approveBtn = this.journalData?.nextStage?.stageName || '';
        this.previousStages = this.journalData?.previousStages?.filter(x => x.stageOrder !== 1);

        this.initializeStageFlags();
        this.showFundsChecked();

        this.journalRequestForm.patchValue({
            returnReason: this.journalData.returnReason,
            matterFromNumber: this.journalData.matterFromNo,
            matterToNumber: this.journalData.matterToNo,
            matterFrom: this.journalData.matterFrom,
            matterTo: this.journalData.matterTo,
            matterFromName: this.journalData.matterFromNo + ' - ' + this.journalData.matterFrom,
            matterToName: this.journalData.matterToNo + ' - ' + this.journalData.matterTo,
            reason: this.journalData.reason,
            amount: this.journalData.amount,
            dateRequested: this.journalData.dateRequested,
            isCheckedLedger: this.journalData.isLedgerChecked ?? false,
            partnerAuthorising: this.journalData.partnerAuthorisingName,
            fundsCheckedByPartner: this.journalData.fundsCheckedByPartner ?? false,
            dateTransaction: this.journalData.dateTransaction ? new Date(this.journalData.dateTransaction) : null,
        });

        if (this.journalData.dateRequested) {
            const d = new Date(this.journalData.dateRequested);
            this.journalRequestForm.get('requestedTime')?.setValue(this.toTimeString(d));
        }

        if (this.journalData?.currentStage?.stageOrder === STAGE_ACCOUNTS) {
            const now = new Date();

            this.journalRequestForm.get('dateTransaction')?.setValue(now);
            this.journalRequestForm.get('transactionTime')?.setValue(this.toTimeString(now));
        }

        this.journalRequestForm.get('partnerAuthorising')?.disable();

        this.configureFormControls();

        [this.actionstepBaseUrl, this.actionstepOverviewBaseUrl] = await Promise.all([
            this.requestService.getActionstepUrl(),
            this.requestService.getActionStepOverviewUrl(),
        ]);

        this.bankAccounts = await this.bankAccountService.getAllBankAccounts();
        await this.filterBankAccounts();

        this.matterFromNo = this.journalData.matterFromNo;
        this.matterToNo = this.journalData.matterToNo;

        this.journalRequestForm.patchValue({
            clientBankAccount1: this.resolveBankAccount(this.journalData.clientBankAccountId1) ?? null,
            clientBankAccount2: this.resolveBankAccount(this.journalData.clientBankAccountId2) ?? null,
        });

        this.updateMatterUrl1();
        this.updateMatterUrl2();

        this.journalRequestForm.get('clientBankAccount1')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.updateMatterUrl1());

        this.journalRequestForm.get('clientBankAccount2')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.updateMatterUrl2());

        if (this.matterFromNo) {
            this.actionstepMatterOverviewUrl1 = `${this.actionstepOverviewBaseUrl}/${this.matterFromNo}`;
            this.showLinkToOpenMatter1.set(true);
        }

        if (this.matterToNo) {
            this.actionstepMatterOverviewUrl2 = `${this.actionstepOverviewBaseUrl}/${this.matterToNo}`;
            this.showLinkToOpenMatter2.set(true);
        }

        const audits = await this.auditService.getUserAuditByRequest(this.journalData.id, RequestTypeOrder[RequestType.Journal]);
        const latestAudit = audits?.length ? audits[audits.length - 1] : null;
        this.isLastAuditRpaSuccessful = latestAudit?.rpaStatus === RpaStatus.Successful;
        this.isRejectionRequested = (latestAudit?.rpaStatus === RpaStatus.Successful && latestAudit.isRejectionRequested === true) ?? false;

        window.addEventListener('storage', this.boundStorageHandler);
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            if (this.isDialogOpen()) {
                const contentEl = this.dialogContent.nativeElement as HTMLElement;
                const currentHeight = contentEl.offsetHeight;
                contentEl.style.height = `${currentHeight}px`;
                contentEl.style.overflowY = 'auto';
            }
        }, 50);
    }

    private async resolveCreatorPartnerApprove(): Promise<void> {
        if (this.journalData.createdByRole) {
            await this.manageUserRoleService
                .getUserPermissionsByRole(this.journalData.createdByRole.toString())
                .then((res: UserRole) => {
                    this.journalData.isCreatorPartnerApprove = res.partnerApprove;

                    if (this.journalData.isCreatorPartnerApprove && this.journalData.previousStages) {
                        this.journalData.previousStages = this.journalData.previousStages.filter(stage => stage.stageOrder !== 2);
                    }
                });
        } else {
            this.journalData.isCreatorPartnerApprove = false;
        }
    }

    private initializeStageFlags(): void {
        const stageId = this.journalData?.currentStage?.stageOrder;
        this.showReturnReason = !!this.journalData?.returnReason;
        this.disableAtStage4 = stageId === STAGE_ACCOUNTS;
        this.enableAtPartnerStage = stageId === STAGE_PARTNER;
        this.enableAtAccountsStage = stageId === STAGE_ACCOUNTS;
        this.disableButtons = stageId === STAGE_COMPLETED;
        this.isAmountReleased = stageId === STAGE_ACCOUNTS;
        this.isAmountReleasedDisabled = stageId === STAGE_COMPLETED;
    }

    private configureFormControls(): void {
        if (this.journalData?.dateAuthorised) {
            this.journalRequestForm.get('dateAuthorized')?.disable();
        }

        if (!this.enableAtPartnerStage) {
            this.journalRequestForm.get('reason')?.disable();
            this.journalRequestForm.get('amount')?.disable();
        }

        if (!(this.enableAtPartnerStage || this.enableAtAccountsStage)) {
            this.journalRequestForm.get('clientBankAccount1')?.disable();
            this.journalRequestForm.get('clientBankAccount2')?.disable();
        }

        this.journalRequestForm.get('matterFromName')?.disable();
        this.journalRequestForm.get('matterToName')?.disable();

        const isCheckedLedgerControl = this.journalRequestForm.get('isCheckedLedger');
        if (this.isAmountReleased) {
            isCheckedLedgerControl?.setValidators([Validators.requiredTrue]);
        } else {
            isCheckedLedgerControl?.clearValidators();
        }

        if (this.isAmountReleasedDisabled) {
            isCheckedLedgerControl?.disable();
        } else {
            isCheckedLedgerControl?.enable();
        }

        if (!this.isDateTransactionVisible) {
            this.journalRequestForm.get('dateTransaction')?.clearValidators();
            this.journalRequestForm.get('dateTransaction')?.updateValueAndValidity();
        }

        const fundsCheckedControl = this.journalRequestForm.get('fundsCheckedByPartner');

        if (this.enableAtPartnerStage) {
            this.showFundsCheckedByPartner = true;

            fundsCheckedControl?.enable();
            fundsCheckedControl?.setValidators([Validators.requiredTrue]);
        }
        else if (this.enableAtAccountsStage) {
            fundsCheckedControl?.disable();
            fundsCheckedControl?.clearValidators();
        }

        fundsCheckedControl?.updateValueAndValidity();
        if (this.journalData?.dateRequested) {
            this.journalRequestForm.get('dateRequested')?.disable();
            this.journalRequestForm.get('requestedTime')?.disable();
        }
    }

    private toTimeString(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    private updateMatterUrl1(): void {
        if (!this.matterFromNo || !this.actionstepBaseUrl) {
            this.actionstepMatterUrl1 = '';
            return;
        }
        const account = this.journalRequestForm?.get('clientBankAccount1')?.value as BankAccount | null;

        this.actionstepMatterUrl1 = `${this.actionstepBaseUrl}?action_id=${this.matterFromNo}&ac_account_id=${account?.accountId ?? ''}`;
    }

    private updateMatterUrl2(): void {
        if (!this.matterToNo || !this.actionstepBaseUrl) {
            this.actionstepMatterUrl2 = '';
            return;
        }
        const account = this.journalRequestForm?.get('clientBankAccount2')?.value as BankAccount | null;
        this.actionstepMatterUrl2 = `${this.actionstepBaseUrl}?action_id=${this.matterToNo}&ac_account_id=${account?.accountId ?? ''}`;
    }

    private resolveBankAccount(accountId?: string): BankAccount | undefined {
        return this.bankAccounts.find(a => a.accountId === accountId);
    }

    get isDateTransactionVisible(): boolean {
        return (this.journalData?.currentStage?.stageOrder ?? 0) >= STAGE_ACCOUNTS;
    }

    get getRequestId(): number | undefined {
        return this.journalData.id;
    }

    get hasAttachments(): boolean {
        return this.journalData?.documentAvailable ?? false;
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event: Event): void {
        this.unlockPopup();
    }

    unlockPopup(): void {
        this.requestService.requestLockorUnLock(this.journalData.id, RequestType.Journal).then((res) => {
            if (res) {
                this.snackbarService.successSnackBar("The request has been unlocked.");
            }
        });
    }

    clearUpload(fileInput: any): void {
        this.displayFile = [];
        this.fileNames = [];
        this.journalRequestForm.get('uploadedFiles')?.setValue([]);
        fileInput.value = '';
    }

    onFilesSelected(input: any): void {
        if (input.files) {
            this.fileNames = [];
            this.displayFile = [];
            const filesArray = Array.from(input.files);

            filesArray.forEach((file: any) => {
                const fileName = file.name;
                this.displayFile.push(fileName);
                const reader = new FileReader();
                reader.onload = (e: any) => {
                    const bytes = e.target.result.split('base64,')[1];
                    this.fileNames.push({ fileName: fileName, attachments: bytes });
                };
                reader.readAsDataURL(file);
            });
        }
        this.journalRequestForm.get('uploadedFiles')?.setValue(this.fileNames);
    }

    uploadDoc(): void {
        if (this.journalRequestForm.get('uploadedFiles')?.value.length > 0) {
            const docData: RequestDocAttachmentResponse = {
                requestId: this.journalData.id!,
                requestType: RequestType.Journal,
                attachments: this.journalRequestForm.get('uploadedFiles')?.value
            }

            this.requestService.addRequestDocument(docData).then((res) => {
                this.snackbarService.successSnackBar(`${res}`);
            });
        }

        if (this.dialogRef) {
            this.dialogRef.close(true);
        }
    }

    closeDialog(): void {
        if (this.dialogRef) {
            this.dialogRef.close(true);
        }

        if (!this.isDialogOpen()) {
            window.close();
        }
    }

    isReturnDisabled(stageId: number): boolean {

        if (!this.isLastAuditRpaSuccessful) {
            return false;
        }

        const currentStageId = this.journalData?.currentStage?.stageOrder;

        if (currentStageId === 4) {
            return false;
        }

        return stageId !== 4 && stageId !== 6;
    }

    showFundsChecked() {
        const stageId = this.journalData?.currentStage?.stageOrder;

        this.showFundsCheckedByPartner = stageId === 3;
        this.FundsCheckedPartnerText = stageId === 4 || stageId === 5;

        if (stageId === 3) {
            this.journalRequestForm.get('fundsCheckedByPartner')?.setValidators([Validators.requiredTrue]);
        }

        if (stageId === 4) {
            this.journalRequestForm.get('fundsCheckedByPartner')?.setValidators([Validators.requiredTrue]);
        }

        this.journalRequestForm.get('fundsCheckedByPartner')?.updateValueAndValidity();
    }

    onUrlCopied() {
        this.snackbarService.successSnackBar("URL copied to clipboard");
    }

    async returnRequest(stageId: number, stageName: string) {
        const audits = await this.auditService.getUserAuditByRequest(this.journalData.id, RequestTypeOrder[RequestType.Journal]);
        const latestAudit = audits?.length ? audits[audits.length - 1] : null;
        const rpaStatus = latestAudit?.rpaStatus === RpaStatus.Successful;
        const dialogRef = this.dialog.open(ReturnReasonDialogComponent, {
            width: '600px',
            data: {
                stageName,
                matterNo: this.journalData?.matterFromNo,
                currentStageId: this.journalData?.currentStage?.id,
                rpaStatus: rpaStatus
            },
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.requestService.returnJournalRequest(this.journalData.id,
                    stageId,
                    result.reason,
                    result.isRPAReversalRequested,
                    result.isRejectionRequested)
                    .then((res) => {
                        if (res) {
                            this.snackbarService.successSnackBar(`${res}`);
                            this.closeDialog();
                        }
                    })
            }
        });
    }

    async delete() {
        const audits = await this.auditService.getUserAuditByRequest(this.journalData.id, RequestTypeOrder[RequestType.Journal]);
        const latestAudit = audits?.length ? audits[audits.length - 1] : null;
        const rpaStatus = latestAudit?.rpaStatus === RpaStatus.Successful;
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '600px',
            data: {
                title: 'Are you sure?',
                message: 'The data associated with the request will be deleted.',
                dialogType: 'warning',
                buttonType: 'ConfirmCancel',
                confirmText: 'Confirm',
                cancelText: 'Cancel',
                matterNo: this.journalData?.matterFromNo,
                currentStageId: this.journalData?.currentStage?.id,
                rpaStatus: rpaStatus
            }
        });

        dialogRef.afterClosed().subscribe((res) => {
            if (res) {
                this.requestService.deleteJournalRequest(this.journalData.id!)
                    .then((res) => {
                        if (res) {
                            this.snackbarService.successSnackBar(`${res}`);
                            this.closeDialog();
                        }
                    })
                    .catch((error) => {
                        this.snackbarService.errorSnackBar("Failed to delete journal request. Please try again.");
                    });
            }

        });
    }

    approveRequest() {
        if (this.journalRequestForm.invalid) {
            this.journalRequestForm.markAllAsTouched();
            return;
        }

        const selectedAccount1 = this.journalRequestForm.get('clientBankAccount1')?.value as BankAccount | null;
        const selectedAccount2 = this.journalRequestForm.get('clientBankAccount2')?.value as BankAccount | null;

        const transferError = validateJournalTransfer(
            Number(this.matterFromNo),
            Number(this.matterToNo),
            selectedAccount1?.accountId ?? this.journalData.clientBankAccountId1,
            selectedAccount2?.accountId ?? this.journalData.clientBankAccountId2
        );

        if (transferError) {
            this.snackbarService.errorSnackBar(transferError);
            return;
        }

        const approvalDetails: ApprovalDetails = {
            dateTransaction: (() => {
                const datePart = this.journalRequestForm.get('dateTransaction')?.value;
                if (!datePart) return undefined;

                const d = datePart instanceof Date ? datePart : new Date(datePart);
                const [hours, minutes] = (
                    this.journalRequestForm.get('transactionTime')?.value ?? '00:00'
                ).split(':').map(Number);

                return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes, 0, 0);
            })(),

            reasonForJournal: this.journalRequestForm.value.reason || this.journalData.reason,
            amount: this.journalRequestForm.value.amount,
            requestDate: this.journalRequestForm.value.requestDate || this.journalData.dateRequested,
            returnReason: undefined,
            isLedgerChecked: this.journalRequestForm.value.isCheckedLedger,
            ledgerCheckedBy: JSON.parse(localStorage.getItem("UserDetails")!).fullName,
            matter: this.journalRequestForm.get('matterFrom')?.value,
            comments: this.journalRequestForm.value.comments,
            fundsCheckedByPartner: this.journalRequestForm.value.fundsCheckedByPartner || this.journalData.fundsCheckedByPartner,
            partnerFundsCheckedBy: JSON.parse(localStorage.getItem("UserDetails")!).fullName,
            clientBankAccountName1: selectedAccount1
                ? `${selectedAccount1.accountId} - ${selectedAccount1.description}`
                : this.journalData.clientBankAccountName1,
            clientBankAccountName2: selectedAccount2
                ? `${selectedAccount2.accountId} - ${selectedAccount2.description}`
                : this.journalData.clientBankAccountName2,
            clientBankAccountId: selectedAccount1?.accountId ?? this.journalData.clientBankAccountId1,
            clientBankAccountId2: selectedAccount2?.accountId ?? this.journalData.clientBankAccountId2,
        };
        this.requestService.approveRequest(this.journalData.id!, RequestType.Journal, this.journalData.nextStage?.id ?? 0, approvalDetails)
            .then((res: string) => {
                if (res) {
                    this.snackbarService.successSnackBar(`${res}`);
                    this.uploadDoc();
                    this.closeDialog()
                }
            })
            .catch((error) => {
                this.snackbarService.errorSnackBar("Failed to approve journal request. Please try again.");
            });
    }


    filterBankAccounts() {
        this.filteredBankAccounts = [...this.bankAccounts];
    }

    downloadDocuments(requestId: number, requestType: string) {
        this.requestService.getRequestDocumentList(requestId, requestType).then((res) => {
            this.fileToBeDownloaded = res;
            this.downloadMultipleBase64Files(this.fileToBeDownloaded.attachments ?? [])
        });

    }

    downloadMultipleBase64Files(files: AttachmentData[]): void {
        files.forEach(file => {
            const { attachments, fileName } = file;
            const byteCharacters = atob(attachments);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/octet-stream' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        });
    }

    @HostListener('window:beforeunload')
    onExpandedTabClose() {
        localStorage.setItem('journalExpandedClosed', Date.now().toString());
    }

    openInNewTab() {
        const id = this.journalData?.id;
        if (!id) return;

        localStorage.setItem('journalExpandedOpen', Date.now().toString());
        const url = this.router.serializeUrl(   
            this.router.createUrlTree([
                '/approvals',
                'journal-request',
                id
            ])
        );
        window.open(window.location.origin + url, '_blank');
    }

    onStorageEvent(event: StorageEvent) {

        if (event.key === 'journalExpandedClosed') {

            this.closeDialog();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        window.removeEventListener('storage', this.boundStorageHandler);
    }
}

